// src/components/admin/MediaKitManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FileText, Image, FileDown, Download, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const ARCA_GREEN = '#6B8E23';

const MediaKitManagement = () => {
    const [activeTab, setActiveTab] = useState('resources');
    const [resources, setResources] = useState([]);
    const [quickFacts, setQuickFacts] = useState([]);
    const [showResourceModal, setShowResourceModal] = useState(false);
    const [showFactModal, setShowFactModal] = useState(false);
    const [editingResource, setEditingResource] = useState(null);
    const [editingFact, setEditingFact] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [resourceForm, setResourceForm] = useState({
        title: '',
        description: '',
        icon: 'Image',
        items: '',
        status: 'Coming Soon',
        download_url: '',
        file_size: '',
        display_order: 0
    });

    const [factForm, setFactForm] = useState({
        label: '',
        value: '',
        display_order: 0
    });

    const API_BASE = process.env.REACT_APP_API_URL;
    if (!API_BASE) {
        throw new Error('API URL not configured');
    }

    const iconOptions = [
        { value: 'Image', label: 'Image Icon' },
        { value: 'FileText', label: 'File Text Icon' },
        { value: 'FileDown', label: 'Download Icon' },
        { value: 'Download', label: 'Download Alt Icon' }
    ];

    useEffect(() => {
        if (activeTab === 'resources') {
            fetchResources();
        } else {
            fetchQuickFacts();
        }
    }, [activeTab]);

    const fetchResources = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/media-kit/resources`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setResources(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching resources:', error);
            toast.error('Failed to load media resources');
        } finally {
            setLoading(false);
        }
    };

    const fetchQuickFacts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/media-kit/quick-facts`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setQuickFacts(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching quick facts:', error);
            toast.error('Failed to load quick facts');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveResource = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');

            // Convert comma-separated items to array
            const itemsArray = resourceForm.items
                .split(',')
                .map(item => item.trim())
                .filter(item => item);

            const dataToSubmit = {
                ...resourceForm,
                items: itemsArray,
                display_order: parseInt(resourceForm.display_order) || 0
            };

            const url = editingResource
                ? `${API_BASE}/admin/media-kit/resources/${editingResource.id}`
                : `${API_BASE}/admin/media-kit/resources`;

            const response = await fetch(url, {
                method: editingResource ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSubmit)
            });

            const data = await response.json();

            if (data.success) {
                setShowResourceModal(false);
                resetResourceForm();
                fetchResources();
                toast.success(`Resource ${editingResource ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving resource:', error);
            toast.error('Failed to save resource');
        }
    };

    const handleSaveFact = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');

            const dataToSubmit = {
                ...factForm,
                display_order: parseInt(factForm.display_order) || 0
            };

            const url = editingFact
                ? `${API_BASE}/admin/media-kit/quick-facts/${editingFact.id}`
                : `${API_BASE}/admin/media-kit/quick-facts`;

            const response = await fetch(url, {
                method: editingFact ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSubmit)
            });

            const data = await response.json();

            if (data.success) {
                setShowFactModal(false);
                resetFactForm();
                fetchQuickFacts();
                toast.success(`Quick fact ${editingFact ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving quick fact:', error);
            toast.error('Failed to save quick fact');
        }
    };

    const handleEditResource = (resource) => {
        setEditingResource(resource);
        setResourceForm({
            title: resource.title || '',
            description: resource.description || '',
            icon: resource.icon || 'Image',
            items: Array.isArray(resource.items) ? resource.items.join(', ') : '',
            status: resource.status || 'Coming Soon',
            download_url: resource.download_url || '',
            file_size: resource.file_size || '',
            display_order: resource.display_order || 0
        });
        setShowResourceModal(true);
    };

    const handleEditFact = (fact) => {
        setEditingFact(fact);
        setFactForm({
            label: fact.label || '',
            value: fact.value || '',
            display_order: fact.display_order || 0
        });
        setShowFactModal(true);
    };

    const handleDeleteResource = async (id) => {
        if (!window.confirm('Are you sure you want to delete this media resource?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/media-kit/resources/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchResources();
                toast.success('Resource deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete resource');
            }
        } catch (error) {
            console.error('Error deleting resource:', error);
            toast.error('Failed to delete resource');
        }
    };

    const handleDeleteFact = async (id) => {
        if (!window.confirm('Are you sure you want to delete this quick fact?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/media-kit/quick-facts/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchQuickFacts();
                toast.success('Quick fact deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete fact');
            }
        } catch (error) {
            console.error('Error deleting quick fact:', error);
            toast.error('Failed to delete quick fact');
        }
    };

    const resetResourceForm = () => {
        setEditingResource(null);
        setResourceForm({
            title: '',
            description: '',
            icon: 'Image',
            items: '',
            status: 'Coming Soon',
            download_url: '',
            file_size: '',
            display_order: 0
        });
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        try {
            setUploading(true);
            const token = localStorage.getItem('adminToken');

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE}/admin-upload/media-kit/file`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                // Update the form with the uploaded file URL and size
                setResourceForm(prev => ({
                    ...prev,
                    download_url: data.data.url,
                    file_size: data.data.formattedSize
                }));
                toast.success('File uploaded successfully!');
            } else {
                toast.error(data.message || 'Failed to upload file');
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
            // Don't reset file input - keep the file name visible
        }
    };

    const resetFactForm = () => {
        setEditingFact(null);
        setFactForm({
            label: '',
            value: '',
            display_order: 0
        });
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h3 mb-1" style={{ color: ARCA_GREEN }}>
                        <FileText size={28} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Media Kit Management
                    </h2>
                    <p className="text-muted mb-0">Manage media resources and quick facts</p>
                </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'resources' ? 'active' : ''}`}
                        onClick={() => setActiveTab('resources')}
                        style={activeTab === 'resources' ? { color: ARCA_GREEN, borderColor: ARCA_GREEN } : {}}
                    >
                        <Image size={18} className="me-2" />
                        Media Resources
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'facts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('facts')}
                        style={activeTab === 'facts' ? { color: ARCA_GREEN, borderColor: ARCA_GREEN } : {}}
                    >
                        <FileText size={18} className="me-2" />
                        Quick Facts
                    </button>
                </li>
            </ul>

            {/* Resources Tab */}
            {activeTab === 'resources' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetResourceForm();
                                setShowResourceModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Media Resource
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Icon</th>
                                                <th>Status</th>
                                                <th>Items Count</th>
                                                <th>File</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resources.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-muted py-4">
                                                        No media resources found
                                                    </td>
                                                </tr>
                                            ) : (
                                                resources.map((resource) => (
                                                    <tr key={resource.id}>
                                                        <td>{resource.display_order}</td>
                                                        <td>
                                                            <strong>{resource.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{resource.description?.substring(0, 60)}...</small>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-secondary">{resource.icon}</span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${resource.status === 'Available' ? 'bg-success' : 'bg-warning'}`}>
                                                                {resource.status}
                                                            </span>
                                                        </td>
                                                        <td>{Array.isArray(resource.items) ? resource.items.length : 0}</td>
                                                        <td>
                                                            {resource.download_url ? (
                                                                <div>
                                                                    <a
                                                                        href={resource.download_url}
                                                                        download
                                                                        className="btn btn-sm btn-success"
                                                                        title={`Download (${resource.file_size || 'File'})`}
                                                                    >
                                                                        <Download size={14} className="me-1" />
                                                                        {resource.file_size || 'Download'}
                                                                    </a>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">No file</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditResource(resource)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteResource(resource.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Quick Facts Tab */}
            {activeTab === 'facts' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetFactForm();
                                setShowFactModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Quick Fact
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Label</th>
                                                <th>Value</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {quickFacts.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted py-4">
                                                        No quick facts found
                                                    </td>
                                                </tr>
                                            ) : (
                                                quickFacts.map((fact) => (
                                                    <tr key={fact.id}>
                                                        <td>{fact.display_order}</td>
                                                        <td><strong>{fact.label}</strong></td>
                                                        <td>
                                                            <span className="badge" style={{ backgroundColor: ARCA_GREEN, fontSize: '1rem' }}>
                                                                {fact.value}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${fact.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                                {fact.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditFact(fact)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteFact(fact.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Resource Modal */}
            {showResourceModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingResource ? 'Edit Media Resource' : 'Add Media Resource'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowResourceModal(false);
                                        resetResourceForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSaveResource}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={resourceForm.title}
                                                onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={resourceForm.display_order}
                                                onChange={(e) => setResourceForm({ ...resourceForm, display_order: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={resourceForm.description}
                                            onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Icon</label>
                                            <select
                                                className="form-select"
                                                value={resourceForm.icon}
                                                onChange={(e) => setResourceForm({ ...resourceForm, icon: e.target.value })}
                                            >
                                                {iconOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={resourceForm.status}
                                                onChange={(e) => setResourceForm({ ...resourceForm, status: e.target.value })}
                                            >
                                                <option value="Coming Soon">Coming Soon</option>
                                                <option value="Available">Available</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Items (comma-separated)</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={resourceForm.items}
                                            onChange={(e) => setResourceForm({ ...resourceForm, items: e.target.value })}
                                            placeholder="e.g., Full color logo, Monochrome logo, Icon only"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Upload File {resourceForm.status === 'Available' && <span className="text-danger">*</span>}</label>
                                        <div className="input-group">
                                            <input
                                                type="file"
                                                className="form-control"
                                                onChange={handleFileUpload}
                                                accept="image/*,.pdf,.zip,.ai,.eps"
                                                disabled={uploading}
                                            />
                                            {uploading && (
                                                <span className="input-group-text">
                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Uploading...</span>
                                                    </div>
                                                </span>
                                            )}
                                        </div>
                                        <div className="form-text">
                                            Supported formats: Images (JPEG, PNG, GIF, WebP, SVG), Documents (PDF, ZIP, AI, EPS). Max 10MB.
                                            {resourceForm.download_url && (
                                                <div className="mt-2 alert alert-success py-2">
                                                    <strong>✓ File uploaded:</strong> {resourceForm.file_size || 'Ready'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowResourceModal(false);
                                            resetResourceForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save Resource
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Fact Modal */}
            {showFactModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingFact ? 'Edit Quick Fact' : 'Add Quick Fact'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowFactModal(false);
                                        resetFactForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSaveFact}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Label *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={factForm.label}
                                            onChange={(e) => setFactForm({ ...factForm, label: e.target.value })}
                                            required
                                            placeholder="e.g., Founded, Member Countries"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Value *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={factForm.value}
                                            onChange={(e) => setFactForm({ ...factForm, value: e.target.value })}
                                            required
                                            placeholder="e.g., 2024, 25+"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Display Order</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={factForm.display_order}
                                            onChange={(e) => setFactForm({ ...factForm, display_order: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowFactModal(false);
                                            resetFactForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save Fact
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaKitManagement;
